import "@/App.css";
import i18n from "@/i18n";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { langFromPath, basenameForLang, stripLangPrefix, localizedPath } from "@/lib/lang";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { CategoriesProvider } from "@/context/CategoriesContext";
import { Header } from "@/components/Header";
import { SeasonalBanner } from "@/components/SeasonalBanner";
import { BackToTop } from "@/components/BackToTop";
// Below-the-fold / on-demand UI is split out of the main bundle to speed up first paint.
const Footer = lazy(() => import("@/components/Footer").then((m) => ({ default: m.Footer })));
const CartDrawer = lazy(() => import("@/components/CartDrawer").then((m) => ({ default: m.CartDrawer })));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent })));
const UnderConstruction = lazy(() => import("@/pages/UnderConstruction"));

import { WebMcpProvider } from "@/components/WebMcpProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";

const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const Admin = lazy(() => import("@/pages/Admin"));
const ProductEditor = lazy(() => import("@/pages/ProductEditor"));
const KannMail = lazy(() => import("@/pages/KannMail"));
const Configurator = lazy(() => import("@/pages/Configurator"));
const ConfiguratorAdmin = lazy(() => import("@/pages/ConfiguratorAdmin"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Account = lazy(() => import("@/pages/Account"));
const YesWeKann = lazy(() => import("@/pages/YesWeKann"));
const Contact = lazy(() => import("@/pages/Contact"));
const About = lazy(() => import("@/pages/About"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Forbidden = lazy(() => import("@/pages/Forbidden"));
const ServerError = lazy(() => import("@/pages/ServerError"));
const BlogEditor = lazy(() => import("@/pages/BlogEditor"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// --- Path-based i18n bootstrap (runs once at module load, before first render) ---
// Backward-compat: redirect legacy ?lang=xx URLs to the path-based equivalent.
(function redirectLegacyLangParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("lang") || "").slice(0, 2);
    if (q && ["nl", "en", "fr", "de"].includes(q)) {
      const clean = stripLangPrefix(window.location.pathname);
      params.delete("lang");
      const qs = params.toString();
      const target = localizedPath(clean, q) + (qs ? `?${qs}` : "") + window.location.hash;
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      if (target !== currentUrl) window.location.replace(target);
    }
  } catch (e) { /* noop */ }
})();

// Drive the app language + router basename from the URL path.
const APP_LANG = langFromPath(window.location.pathname);
if (i18n.language !== APP_LANG) i18n.changeLanguage(APP_LANG);
const APP_BASENAME = basenameForLang(APP_LANG);

function PageFallback() {
  return <div className="min-h-screen" aria-busy="true" />;
}

function Layout() {
  const location = useLocation();
  const { settings } = useSettings();
  const { isAdmin: isAdminUser } = useAuth();
  const path = location.pathname;
  const isAdmin_route = path.startsWith("/admin");
  const isEditor = path.startsWith("/admin/products") || path.startsWith("/admin/blog") || path.startsWith("/admin/mail") || path.startsWith("/admin/configurator");
  const isAuthRoute = ["/login", "/register", "/forgot-password", "/reset-password", "/verify-email"].some((p) => path.startsWith(p));

  // Under-construction gate: everyone except admins (and admin/auth routes) sees the maintenance screen.
  const maintenance = !!settings?.maintenance?.enabled;
  if (maintenance && !isAdminUser && !isAdmin_route && !isAuthRoute) {
    return (
      <>
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={null}>
            <UnderConstruction />
          </Suspense>
        </ErrorBoundary>
      </>
    );
  }

  const isAdmin = isAdmin_route;
  return (
    <>
      <ScrollToTop />
      <WebMcpProvider />
      {!isEditor && !isAdmin && <SeasonalBanner />}
      {!isEditor && <Header />}
      <main className="min-h-screen" style={{ paddingTop: "var(--season-banner-h, 0px)" }}>
        <ErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:category" element={<Shop />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/configurator" element={<Configurator />} />
            <Route path="/order-success" element={<OrderSuccess />} />
            <Route path="/yes-we-kann" element={<YesWeKann />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/cookies" element={<LegalPage />} />
            <Route path="/disclaimer" element={<LegalPage />} />
            <Route path="/returns" element={<LegalPage />} />
            <Route path="/shipping" element={<LegalPage />} />
            <Route path="/legal-notice" element={<LegalPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
            <Route path="/admin/products/new" element={<ProtectedRoute admin><ProductEditor /></ProtectedRoute>} />
            <Route path="/admin/products/:id" element={<ProtectedRoute admin><ProductEditor /></ProtectedRoute>} />
            <Route path="/admin/mail" element={<ProtectedRoute admin><KannMail /></ProtectedRoute>} />
            <Route path="/admin/configurator" element={<ProtectedRoute admin><ConfiguratorAdmin /></ProtectedRoute>} />

            <Route path="/admin/blog/new" element={<ProtectedRoute admin><BlogEditor /></ProtectedRoute>} />
            <Route path="/admin/blog/:id" element={<ProtectedRoute admin><BlogEditor /></ProtectedRoute>} />
            <Route path="/403" element={<Forbidden />} />
            <Route path="/500" element={<ServerError />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>
      <Suspense fallback={null}>
        {!isAdmin && <Footer />}
        <CartDrawer />
        <CookieConsent />
      </Suspense>
      {!isEditor && <BackToTop />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <SettingsProvider>
          <CategoriesProvider>
          <AuthProvider>
            <NotificationsProvider>
              <CartProvider>
                <BrowserRouter basename={APP_BASENAME}>
                  <Layout />
                  <Toaster position="bottom-right" duration={2500} />
                </BrowserRouter>
              </CartProvider>
            </NotificationsProvider>
          </AuthProvider>
          </CategoriesProvider>
        </SettingsProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
